function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import set from 'lodash/set';
import pick from 'lodash/pick';
import useStyles from 'isomorphic-style-loader/useStyles';
import { getStandards } from 'helpers/standards';
import { useAction } from 'hooks';
import Form from 'components/Form';
import FormGroup from 'components/Form/FormGroup';
import AutosuggestInput from 'components/Form/AutosuggestInput';
import App, { AlertsBus } from 'modules/App';
import Account from 'modules/Account';
import CloudDrive from 'modules/CloudDrive';
import messages from '../../../messages';
import * as actions from '../../../actions';
import PhiSetFormPartial from './PhiSetFormPartial';
import Actions from './Actions';
import Gender from './Gender';
import GlucoseLevelUnitTypes from './GlucoseLevelUnitTypes';
import { useIsGlucoseLevelUnitTypesRequired, usePatientProfileEffects, usePatientProfileForm } from './hooks';
import styles from './patientProfile.pcss';
const PatientProfile = ({ enrollCode, invitationCode, hasCentralStorage })=>{
    useStyles(styles);
    const account = useSelector(Account.selectors.account);
    const countrySettings = useSelector(Account.selectors.countrySettings);
    const authorizationCode = useSelector(CloudDrive.selectors.authorizationCode);
    const payers = useSelector(Account.selectors.payers);
    const patientSetup = useAction(actions.patientSetup);
    const addPayerProposal = useAction(App.actions.addPayerProposal);
    const { formName, formValues, setFormValue, setFormValues } = usePatientProfileForm();
    const isGlucoseLevelUnitTypesRequired = useIsGlucoseLevelUnitTypesRequired();
    useEffect(()=>{
        if (!hasCentralStorage) {
            setFormValues({
                diabetesType: 'NoResponse',
                treatmentType: 'NoResponse'
            });
        }
    }, []);
    let validatorRules = {};
    if (!hasCentralStorage) {
        validatorRules = _object_spread_props(_object_spread({}, validatorRules), {
            gender: 'required',
            diabetesType: 'required',
            treatmentType: 'required'
        });
        if (isGlucoseLevelUnitTypesRequired) {
            set(validatorRules, 'glucoseLevelUnitTypes', 'required');
        }
    }
    const isPayerMandatory = countrySettings ? countrySettings.isPayerMandatory : false;
    if (isPayerMandatory) {
        set(validatorRules, 'payer', 'required');
    }
    const standards = getStandards(null, countrySettings);
    const submitForm = (validatedValues)=>{
        const createPatientValues = pick(validatedValues, [
            'diabetesType',
            'treatmentType',
            'sharingRequestId',
            'payer'
        ]);
        const createPatientProfile = {
            createPatientValues,
            authorizationCode,
            standards,
            enrollCode,
            invitationCode
        };
        const informationValues = pick(validatedValues, [
            'gender'
        ]);
        const updateAccountSettings = isGlucoseLevelUnitTypesRequired ? pick(validatedValues, [
            'glucoseLevelUnitTypes'
        ]) : null;
        patientSetup(createPatientProfile, informationValues, updateAccountSettings);
        const { payer } = validatedValues;
        if (payer) {
            const { countryId } = account;
            addPayerProposal(countryId, payer);
        }
    };
    const processForm = App.hooks.useFormProcessing(formName, submitForm);
    const onSubmit = ()=>{
        processForm(formValues, validatorRules);
    };
    usePatientProfileEffects();
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": "fill-health-info-section",
        children: [
            /*#__PURE__*/ _jsx("header", {
                className: "pageHeader",
                children: /*#__PURE__*/ _jsx("h1", {
                    children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.headers.fillHealthInformation))
                })
            }),
            /*#__PURE__*/ _jsx(AlertsBus, {}),
            /*#__PURE__*/ _jsx("p", {
                className: styles.intro,
                children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.intros.fillHealthInformation))
            }),
            /*#__PURE__*/ _jsxs(Form, {
                onSubmit: onSubmit,
                children: [
                    !hasCentralStorage && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Gender, {}),
                            /*#__PURE__*/ _jsx(PhiSetFormPartial, {}),
                            /*#__PURE__*/ _jsx(GlucoseLevelUnitTypes, {})
                        ]
                    }),
                    isPayerMandatory && /*#__PURE__*/ _jsx(FormGroup, {
                        id: "payer",
                        labelMessage: Account.messages.labels.payer,
                        formValues: formValues,
                        children: /*#__PURE__*/ _jsx(AutosuggestInput, {
                            suggestions: payers,
                            placeholder: Account.messages.placeholders.payer,
                            onChange: setFormValue
                        })
                    }),
                    /*#__PURE__*/ _jsx(Actions, {
                        validatorRules: validatorRules
                    })
                ]
            })
        ]
    });
};
export default PatientProfile;
